/**
 * Claude Activity Store
 *
 * Polls the Rust backend to check if Claude Code is actively working
 * on each project that has open terminals. Uses session file monitoring
 * (not terminal output) for reliable detection.
 *
 * Polling is adaptive: 5s while at least one watched project shows recent
 * activity (within ACTIVE_WINDOW_MS), 30s otherwise. Cuts the steady-state
 * IO load by ~6x when nothing is happening, which is most of the time.
 */

import { create } from 'zustand';
import { logger } from '@/lib/logger';

interface ClaudeActivityStatus {
  has_sessions: boolean;
  is_active: boolean;
  seconds_since_activity: number;
  last_stop_reason: string | null;
}

interface ClaudeActivityState {
  /** project root_path → activity status */
  activities: Map<string, ClaudeActivityStatus>;
}

export const useClaudeActivityStore = create<ClaudeActivityState>(() => ({
  activities: new Map(),
}));

const FAST_POLL_MS = 5_000;
const SLOW_POLL_MS = 30_000;
/** Stay in fast-poll mode if any project saw activity in this window. */
const ACTIVE_WINDOW_MS = 60_000;

let pollTimeout: ReturnType<typeof setTimeout> | null = null;
let lastObservedActivityAt = 0;

async function pollClaudeActivity(): Promise<void> {
  // Session-file activity detection rode the Tauri-only `check_claude_activity`
  // command, which the CEF host/core does not expose. Until a `session.*` core
  // handler lands (a separate ticket), there is no source to poll — keep the
  // activity map empty so the sidebar dots stay idle rather than erroring.
  useClaudeActivityStore.setState({ activities: new Map() });
}

/**
 * Schedule the next poll based on whether anything's happened recently.
 * setTimeout (not setInterval) so the cadence can change without juggling
 * separate handles.
 */
function scheduleNextPoll(): void {
  if (pollTimeout) {
    // Already scheduled — let it fire.
    return;
  }
  const elapsed = Date.now() - lastObservedActivityAt;
  const delay = elapsed < ACTIVE_WINDOW_MS ? FAST_POLL_MS : SLOW_POLL_MS;
  pollTimeout = setTimeout(async () => {
    pollTimeout = null;
    await pollClaudeActivity();
    // Re-arm only if polling hasn't been stopped in the meantime.
    if (pollingStarted) scheduleNextPoll();
  }, delay);
}

let pollingStarted = false;

export function startClaudeActivityPolling(): void {
  if (pollingStarted) return;
  pollingStarted = true;
  // Initial check, then schedule the next one based on what it sees.
  void pollClaudeActivity().finally(() => {
    if (pollingStarted) scheduleNextPoll();
  });
  logger.debug('ClaudeActivity', 'Polling started (adaptive)');
}

export function stopClaudeActivityPolling(): void {
  pollingStarted = false;
  if (pollTimeout) {
    clearTimeout(pollTimeout);
    pollTimeout = null;
  }
  logger.debug('ClaudeActivity', 'Polling stopped');
}
